import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { api } from '@/lib/api';
import { wsClient } from '@/lib/websocket';
import { useAuthStore } from '@/stores/auth.store';

// ============ Types ============

export interface Message {
  _id: string;
  conversationId: string;
  type: 'text' | 'image' | 'file' | 'audio' | 'video' | 'system' | 'approval' | 'link';
  status: 'sending' | 'sent' | 'delivered' | 'read' | 'failed';
  content: string;
  contentHtml?: string;
  sender: {
    userId: string;
    displayName: string;
    avatar?: string;
    role?: string;
  };
  attachments?: {
    type: 'image' | 'document' | 'audio' | 'video' | 'other';
    name: string;
    url: string;
    size: number;
    mimeType: string;
    thumbnailUrl?: string;
  }[];
  reactions?: {
    emoji: string;
    userId: string;
    createdAt: string;
  }[];
  mentions?: {
    userId: string;
    displayName: string;
    startIndex: number;
    endIndex: number;
  }[];
  replyTo?: {
    _id: string;
    content: string;
    sender: {
      displayName: string;
    };
  };
  isEdited: boolean;
  isDeleted: boolean;
  isClientVisible: boolean;
  readBy?: {
    userId: string;
    at: string;
  }[];
  createdAt: string;
  updatedAt: string;
}

export interface Conversation {
  _id: string;
  code: string;
  type: 'direct' | 'group' | 'client_channel' | 'job_thread' | 'approval_thread' | 'support_ticket' | 'system';
  title?: string;
  description?: string;
  participants: string[];
  admins: string[];
  participantCount: number;
  status: 'active' | 'archived' | 'closed';
  isClientVisible: boolean;
  clientId?: string;
  context?: {
    type: string;
    entityId: string;
    entityName?: string;
  };
  lastMessageAt?: string;
  lastMessagePreview?: string;
  messageCount: number;
  settings: {
    allowFileUpload: boolean;
    allowReactions: boolean;
    requireApprovalToJoin: boolean;
    maxParticipants: number;
    messageRetentionDays?: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface TypingUser {
  userId: string;
  userName: string;
}

export interface ChatState {
  // Conversations
  conversations: Conversation[];
  activeConversationId: string | null;
  conversationsLoading: boolean;

  // Messages
  messages: Map<string, Message[]>;
  messagesLoading: boolean;
  hasMoreMessages: Map<string, boolean>;

  // Typing
  typingUsers: Map<string, TypingUser[]>;

  // Unread
  unreadCounts: Map<string, number>;
  totalUnread: number;

  // Presence
  onlineUsers: Set<string>;

  // UI State
  isChatOpen: boolean;

  // Actions
  fetchConversations: () => Promise<void>;
  fetchMessages: (conversationId: string, loadMore?: boolean) => Promise<void>;
  setActiveConversation: (conversationId: string | null) => void;
  sendMessage: (conversationId: string, content: string, options?: {
    attachments?: unknown[];
    replyTo?: string;
    mentions?: unknown[];
  }) => Promise<void>;
  markAsRead: (conversationId: string) => Promise<void>;
  addReaction: (messageId: string, emoji: string) => void;
  removeReaction: (messageId: string, emoji: string) => void;
  startTyping: (conversationId: string) => void;
  stopTyping: (conversationId: string) => void;
  toggleChat: () => void;
  openChat: () => void;
  closeChat: () => void;
  createConversation: (params: {
    type: 'direct' | 'group' | 'client_channel' | 'job_thread';
    title?: string;
    participantIds: string[];
    clientId?: string;
    context?: { type: string; entityId: string; };
    isClientVisible?: boolean;
  }) => Promise<Conversation | null>;
  startPolling: () => void;
  stopPolling: () => void;

  // Tracks which conversations were fully loaded via fetchMessages API (not just WS drip)
  fetchedConversationIds: Set<string>;

  // Internal actions for WebSocket updates
  _addMessage: (message: Message) => void;
  _updateMessage: (messageId: string, updates: Partial<Message>) => void;
  _removeMessage: (messageId: string, conversationId: string) => void;
  _setTypingUsers: (conversationId: string, users: TypingUser[]) => void;
  _addTypingUser: (conversationId: string, user: TypingUser) => void;
  _removeTypingUser: (conversationId: string, userId: string) => void;
  _setOnlineUser: (userId: string, isOnline: boolean) => void;
  _updateUnreadCounts: (counts: Record<string, number>) => void;
  _updateConversation: (conversation: Partial<Conversation> & { _id: string }) => void;
}

// ============ Store ============

export const useChatStore = create<ChatState>()(
  subscribeWithSelector((set, get) => {
    // Polling interval for when WebSocket is disconnected
    let pollingInterval: number | null = null;

    const stopPolling = () => {
      if (pollingInterval) {
        console.log('[Chat Store] ⏹️ Stopping message polling');
        clearInterval(pollingInterval);
        pollingInterval = null;
      }
    };

    const startPolling = () => {
      // POLLING DISABLED — testing pure WebSocket delivery
      // if (pollingInterval) return;
      // if (wsClient.isConnected) return;
      // pollingInterval = window.setInterval(() => { ... }, 10000);
      console.log('[Chat Store] ℹ️ Polling disabled for WebSocket testing');
    };

    return {
      // Initial state
      conversations: [],
      activeConversationId: null,
      conversationsLoading: false,
      messages: new Map(),
      fetchedConversationIds: new Set(),
      messagesLoading: false,
      hasMoreMessages: new Map(),
      typingUsers: new Map(),
      unreadCounts: new Map(),
      totalUnread: 0,
      onlineUsers: new Set(),
      isChatOpen: false,

      startPolling,
      stopPolling,

      // Actions
      fetchConversations: async () => {
        console.log('[Chat Store] 📋 Fetching conversations...');
        set({ conversationsLoading: true });
        try {
          const response = await api.get<{ conversations: Conversation[] }>('/communication/conversations');
          if (response.success && response.data) {
            const convs = response.data.conversations;
            console.log('[Chat Store] ✅ Conversations loaded:', { count: convs.length });
            set({ conversations: convs });

            // Subscribe to all conversations so chat list updates in real-time
            // even when the conversation is not currently open
            if (wsClient.isConnected) {
              console.log(`[Chat Store] 📡 Subscribing to ${convs.length} conversations for list updates`);
              convs.forEach(c => wsClient.subscribe(c._id));
            }
          }
        } catch (error) {
          console.error('[Chat Store] ❌ Failed to fetch conversations:', error);
        } finally {
          set({ conversationsLoading: false });
        }
      },

      fetchMessages: async (conversationId: string, loadMore = false) => {
        console.log('[Chat Store] 💬 Fetching messages:', { conversationId, loadMore });
        set({ messagesLoading: true });
        try {
          const existingMessages = get().messages.get(conversationId) || [];
          const before = loadMore && existingMessages.length > 0
            ? existingMessages[0]._id
            : undefined;

          const response = await api.get<{
            messages: Message[];
            hasMore: boolean;
          }>(`/communication/conversations/${conversationId}/messages`, {
            params: { before, limit: 50 },
          });

          if (response.success && response.data) {
            // Messages come newest-first from API, reverse for display (oldest first)
            const newMessages = [...response.data.messages].reverse();
            console.log('[Chat Store] ✅ Messages loaded:', {
              conversationId,
              count: newMessages.length,
              hasMore: response.data.hasMore,
              loadMore
            });
            const messages = new Map(get().messages);

            if (loadMore) {
              // Prepend older messages at the beginning
              messages.set(conversationId, [...newMessages, ...existingMessages]);
            } else {
              messages.set(conversationId, newMessages);
              // Subscribe to WebSocket updates
              wsClient.subscribe(conversationId);
            }

            const hasMoreMessages = new Map(get().hasMoreMessages);
            hasMoreMessages.set(conversationId, response.data.hasMore);

            // Mark as fully fetched so setActiveConversation won't re-fetch
            const fetchedConversationIds = new Set(get().fetchedConversationIds);
            fetchedConversationIds.add(conversationId);

            set({ messages, hasMoreMessages, fetchedConversationIds });
          }
        } catch (error) {
          console.error('[Chat Store] ❌ Failed to fetch messages:', error);
        } finally {
          set({ messagesLoading: false });
        }
      },

      setActiveConversation: (conversationId: string | null) => {
        const prevConversationId = get().activeConversationId;

        // Unsubscribe from previous conversation
        if (prevConversationId && prevConversationId !== conversationId) {
          wsClient.unsubscribe(prevConversationId);
          get().stopTyping(prevConversationId);
        }

        set({ activeConversationId: conversationId });

        if (conversationId) {
          // Fetch full message history if this conversation was never properly fetched.
          // messages.has() is NOT enough — WS drip messages create entries without a full fetch.
          if (!get().fetchedConversationIds.has(conversationId)) {
            get().fetchMessages(conversationId);
          } else {
            // Already fully loaded; just ensure WS subscription is active
            wsClient.subscribe(conversationId);
          }
          // Mark as read
          get().markAsRead(conversationId);

          // Start polling if WebSocket is not connected
          if (!wsClient.isConnected) {
            startPolling();
          }
        } else {
          // No active conversation, stop polling
          stopPolling();
        }
      },

      sendMessage: async (conversationId: string, content: string, options) => {
        // Get conversation details for logging
        const conversation = get().conversations.find(c => c._id === conversationId);

        // For client_channel, receiver is the clientId, not in participants
        const receivers = conversation?.type === 'client_channel'
          ? (conversation.clientId ? [conversation.clientId] : [])
          : conversation?.participants?.filter(p => p !== conversation?.participants?.[0]);

        console.log('[Chat Store] 📤 Sending message:', {
          conversationId,
          conversationType: conversation?.type,
          contentLength: content.length,
          hasAttachments: !!options?.attachments?.length,
          hasReplyTo: !!options?.replyTo,
          participants: conversation?.participants,
          participantCount: conversation?.participantCount || conversation?.participants?.length || 0,
          clientId: conversation?.clientId,
          receivers: receivers
        });
        try {
          // Use HTTP API to send message (more reliable than WebSocket)
          const response = await api.post<{ message: Message }>(`/communication/conversations/${conversationId}/messages`, {
            content,
            type: 'text',
            attachments: options?.attachments,
            replyTo: options?.replyTo,
            mentions: options?.mentions,
          });

          if (response.success && response.data) {
            // Add message to local state
            const newMessage = response.data.message;

            // For client_channel, receiver is the clientId
            const messageReceivers = conversation?.type === 'client_channel'
              ? (conversation.clientId ? [conversation.clientId] : [])
              : conversation?.participants?.filter(p => p !== newMessage.sender?.userId);

            console.log('[Chat Store] ✅ Message sent successfully:', {
              messageId: newMessage._id,
              conversationId,
              conversationType: conversation?.type,
              sender: {
                userId: newMessage.sender?.userId,
                displayName: newMessage.sender?.displayName,
                role: newMessage.sender?.role
              },
              receivers: messageReceivers,
              clientId: conversation?.clientId,
              allParticipants: conversation?.participants
            });
            const messages = new Map(get().messages);
            const conversationMessages = messages.get(conversationId) || [];
            if (!conversationMessages.some(m => m._id === newMessage._id)) {
              messages.set(conversationId, [...conversationMessages, newMessage]);
              set({ messages }); // Update state so message appears in UI
            }

            // Update conversation's last message and re-sort newest first
            const conversations = get().conversations
              .map(c => {
                if (c._id === conversationId) {
                  return {
                    ...c,
                    lastMessageAt: newMessage.createdAt,
                    lastMessagePreview: newMessage.content.substring(0, 100),
                    updatedAt: newMessage.createdAt,
                  };
                }
                return c;
              })
              .sort((a, b) => {
                const ta = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : new Date(a.createdAt).getTime();
                const tb = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : new Date(b.createdAt).getTime();
                return tb - ta;
              });
            set({ conversations });
          }
        } catch (error) {
          console.error('[Chat Store] ❌ Failed to send message via HTTP, trying WebSocket:', error);
          // Fallback to WebSocket
          wsClient.sendMessage(conversationId, content, options);
        }
      },

      markAsRead: async (conversationId: string) => {
        // Optimistically update unread count first
        const unreadCounts = new Map(get().unreadCounts);
        unreadCounts.set(conversationId, 0);
        const totalUnread = Array.from(unreadCounts.values()).reduce((sum, count) => sum + count, 0);
        set({ unreadCounts, totalUnread });

        // Try HTTP API first, then fallback to WebSocket
        try {
          await api.post(`/communication/conversations/${conversationId}/read`);
        } catch {
          // Silently try WebSocket as fallback (may also fail if not connected)
          try {
            wsClient.markAsRead(conversationId);
          } catch {
            // Ignore - read status will sync on next fetch
          }
        }
      },

      addReaction: (messageId: string, emoji: string) => {
        // Only attempt if WebSocket is connected
        try {
          wsClient.addReaction(messageId, emoji);
        } catch {
          console.warn('Cannot add reaction: WebSocket not connected');
        }
      },

      removeReaction: (messageId: string, emoji: string) => {
        try {
          wsClient.removeReaction(messageId, emoji);
        } catch {
          console.warn('Cannot remove reaction: WebSocket not connected');
        }
      },

      startTyping: (conversationId: string) => {
        try {
          wsClient.startTyping(conversationId);
        } catch {
          // Ignore - typing indicator is not critical
        }
      },

      stopTyping: (conversationId: string) => {
        try {
          wsClient.stopTyping(conversationId);
        } catch {
          // Ignore - typing indicator is not critical
        };
      },

      toggleChat: () => set((state) => ({ isChatOpen: !state.isChatOpen })),
      openChat: () => set({ isChatOpen: true }),
      closeChat: () => set({ isChatOpen: false }),

      createConversation: async (params) => {
        console.log('[Chat Store] 🆕 Creating conversation:', {
          type: params.type,
          title: params.title,
          participantIds: params.participantIds,
          participantCount: params.participantIds?.length,
          hasClientId: !!params.clientId,
          clientId: params.clientId,
          isClientVisible: params.isClientVisible
        });
        try {
          // Build participants array - first user is owner, others are members
          const participants = (params.participantIds || []).map((id, index) => ({
            userId: id,
            role: index === 0 ? 'owner' as const : 'member' as const,
          }));

          if (participants.length === 0) {
            console.error('[Chat Store] ❌ No participants provided for conversation');
            return null;
          }

          console.log('[Chat Store] 👥 Participants being added:', {
            owner: participants[0]?.userId,
            members: participants.slice(1).map(p => p.userId),
            allParticipants: participants.map(p => ({ userId: p.userId, role: p.role }))
          });

          const response = await api.post<{ conversation: Conversation }>('/communication/conversations', {
            type: params.type,
            title: params.title,
            participants,
            clientId: params.clientId,
            context: params.context,
            isClientVisible: params.isClientVisible ?? true,
          });

          if (response.success && response.data) {
            const newConversation = response.data.conversation;
            console.log('[Chat Store] ✅ Conversation created:', {
              id: newConversation._id,
              type: newConversation.type,
              title: newConversation.title,
              participants: newConversation.participants,
              participantCount: newConversation.participants.length,
              clientId: newConversation.clientId,
              admins: newConversation.admins
            });

            // Check if conversation already exists in the list to prevent duplicates
            const existingConversations = get().conversations;
            const alreadyExists = existingConversations.some(c => c._id === newConversation._id);

            if (alreadyExists) {
              console.log('[Chat Store] ℹ️ Conversation already exists, setting as active');
              set({
                activeConversationId: newConversation._id,
                isChatOpen: true,
              });
            } else {
              console.log('[Chat Store] ➕ Adding new conversation to list');
              set((state) => ({
                conversations: [newConversation, ...state.conversations],
                activeConversationId: newConversation._id,
                isChatOpen: true,
              }));
            }
            // Always fetch messages when activating a conversation via create
            if (!get().messages.has(newConversation._id)) {
              get().fetchMessages(newConversation._id);
            } else {
              wsClient.subscribe(newConversation._id);
            }
            return newConversation;
          }
          return null;
        } catch (error) {
          console.error('[Chat Store] ❌ Failed to create conversation:', error);
          return null;
        }
      },

      // Internal actions for WebSocket updates
      _addMessage: (message: Message) => {
        // Get conversation for participant info
        const conversation = get().conversations.find(c => c._id === message.conversationId);

        // For client_channel, receiver is the clientId, not in participants
        const receivers = conversation?.type === 'client_channel'
          ? (conversation.clientId ? [conversation.clientId] : [])
          : conversation?.participants?.filter(p => p !== message.sender?.userId);

        console.log('[Chat Store] 📨 Received new message:', {
          messageId: message._id,
          conversationId: message.conversationId,
          conversationType: conversation?.type,
          sender: {
            userId: message.sender?.userId,
            displayName: message.sender?.displayName,
            role: message.sender?.role
          },
          receivers: receivers,
          clientId: conversation?.clientId,
          allParticipants: conversation?.participants,
          participantCount: conversation?.participantCount || conversation?.participants?.length || 0,
          contentPreview: message.content.substring(0, 50) + '...'
        });
        const messages = new Map(get().messages);
        const conversationMessages = messages.get(message.conversationId) || [];

        // Check if message already exists
        if (!conversationMessages.some(m => m._id === message._id)) {
          messages.set(message.conversationId, [...conversationMessages, message]);
          set({ messages });

          // Update conversation preview
          get()._updateConversation({
            _id: message.conversationId,
            lastMessageAt: message.createdAt,
            lastMessagePreview: message.content.substring(0, 100),
          });

          // Update unread if not active conversation
          if (message.conversationId !== get().activeConversationId) {
            const unreadCounts = new Map(get().unreadCounts);
            unreadCounts.set(
              message.conversationId,
              (unreadCounts.get(message.conversationId) || 0) + 1
            );
            const totalUnread = Array.from(unreadCounts.values()).reduce((sum, count) => sum + count, 0);
            console.log('[Chat Store] 🔔 Unread count updated:', { conversationId: message.conversationId, totalUnread });
            set({ unreadCounts, totalUnread });
          }
        } else {
          console.log('[Chat Store] ⏭️ Message already exists, skipping');
        }
      },

      _updateMessage: (messageId: string, updates: Partial<Message>) => {
        const messages = new Map(get().messages);
        messages.forEach((conversationMessages, conversationId) => {
          const index = conversationMessages.findIndex(m => m._id === messageId);
          if (index !== -1) {
            const updated = [...conversationMessages];
            updated[index] = { ...updated[index], ...updates };
            messages.set(conversationId, updated);
          }
        });
        set({ messages });
      },

      _removeMessage: (messageId: string, conversationId: string) => {
        const messages = new Map(get().messages);
        const conversationMessages = messages.get(conversationId) || [];
        messages.set(
          conversationId,
          conversationMessages.filter(m => m._id !== messageId)
        );
        set({ messages });
      },

      _setTypingUsers: (conversationId: string, users: TypingUser[]) => {
        const typingUsers = new Map(get().typingUsers);
        typingUsers.set(conversationId, users);
        set({ typingUsers });
      },

      _addTypingUser: (conversationId: string, user: TypingUser) => {
        const typingUsers = new Map(get().typingUsers);
        const existing = typingUsers.get(conversationId) || [];
        if (!existing.some(u => u.userId === user.userId)) {
          typingUsers.set(conversationId, [...existing, user]);
          set({ typingUsers });
        }
      },

      _removeTypingUser: (conversationId: string, userId: string) => {
        const typingUsers = new Map(get().typingUsers);
        const existing = typingUsers.get(conversationId) || [];
        typingUsers.set(conversationId, existing.filter(u => u.userId !== userId));
        set({ typingUsers });
      },

      _setOnlineUser: (userId: string, isOnline: boolean) => {
        const onlineUsers = new Set(get().onlineUsers);
        if (isOnline) {
          onlineUsers.add(userId);
        } else {
          onlineUsers.delete(userId);
        }
        set({ onlineUsers });
      },

      _updateUnreadCounts: (counts: Record<string, number>) => {
        // Server includes a '_total' key — exclude it to avoid double-counting
        const entries = Object.entries(counts).filter(([k]) => k !== '_total');
        const unreadCounts = new Map(entries);
        const totalUnread = entries.reduce((sum, [, v]) => sum + v, 0);
        set({ unreadCounts, totalUnread });
      },

      _updateConversation: (updates: Partial<Conversation> & { _id: string }) => {
        set((state) => {
          const updated = state.conversations.map(c =>
            c._id === updates._id ? { ...c, ...updates } : c
          );
          // Re-sort: newest lastMessageAt first
          updated.sort((a, b) => {
            const ta = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : new Date(a.createdAt).getTime();
            const tb = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : new Date(b.createdAt).getTime();
            return tb - ta;
          });
          return { conversations: updated };
        });
      },
    };
  })
);

// ============ WebSocket Event Handlers ============

// Handle new messages
wsClient.on('message:new', (payload) => {
  const message = payload as Message;
  console.log('[Chat Store WS] 📨 message:new event:', { messageId: message._id, conversationId: message.conversationId });
  const state = useChatStore.getState();

  // If this conversation isn't loaded yet (other party created it), refresh the list
  const convExists = state.conversations.some(c => c._id === message.conversationId);
  if (!convExists) {
    console.log('[Chat Store WS] 🆕 Unknown conversation — refreshing list');
    state.fetchConversations();
    return;
  }

  state._addMessage(message);
});

// Handle sent confirmation
wsClient.on('message:sent', (payload) => {
  const { message } = payload as { message: Message };
  console.log('[Chat Store WS] ✅ message:sent event:', { messageId: message._id, conversationId: message.conversationId });
  useChatStore.getState()._addMessage(message);
});

// Handle message updates
wsClient.on('message:updated', (payload) => {
  const { messageId, content, editedAt } = payload as {
    messageId: string;
    content: string;
    editedAt: string;
  };
  console.log('[Chat Store WS] ✏️ message:updated event:', { messageId });
  useChatStore.getState()._updateMessage(messageId, {
    content,
    isEdited: true,
    updatedAt: editedAt,
  });
});

// Handle message deletion
wsClient.on('message:removed', (payload) => {
  const { messageId } = payload as { messageId: string };
  console.log('[Chat Store WS] 🗑️ message:removed event:', { messageId });
  // Get conversation ID from messages
  const messages = useChatStore.getState().messages;
  messages.forEach((msgs, conversationId) => {
    if (msgs.some(m => m._id === messageId)) {
      useChatStore.getState()._updateMessage(messageId, {
        isDeleted: true,
        content: 'This message was deleted',
      });
    }
  });
});

// Handle reactions
wsClient.on('reaction:added', (payload) => {
  const { messageId, reaction } = payload as {
    messageId: string;
    reaction: { emoji: string; userId: string };
  };
  // Update message reactions
  const messages = useChatStore.getState().messages;
  messages.forEach((msgs, conversationId) => {
    const msg = msgs.find(m => m._id === messageId);
    if (msg) {
      useChatStore.getState()._updateMessage(messageId, {
        reactions: [
          ...(msg.reactions || []),
          { ...reaction, createdAt: new Date().toISOString() },
        ],
      });
    }
  });
});

wsClient.on('reaction:removed', (payload) => {
  const { messageId, reaction } = payload as {
    messageId: string;
    reaction: { emoji: string; userId: string };
  };
  const messages = useChatStore.getState().messages;
  messages.forEach((msgs, conversationId) => {
    const msg = msgs.find(m => m._id === messageId);
    if (msg) {
      useChatStore.getState()._updateMessage(messageId, {
        reactions: (msg.reactions || []).filter(
          r => !(r.emoji === reaction.emoji && r.userId === reaction.userId)
        ),
      });
    }
  });
});

// Handle read receipts — update own message statuses when the other person reads
wsClient.on('messages:read', (payload) => {
  const { upToMessageId } = payload as { userId: string; readAt: string; upToMessageId?: string };
  const currentUserId = useAuthStore.getState().user?.id;
  if (!currentUserId) return;

  const state = useChatStore.getState();
  state.messages.forEach((msgs) => {
    msgs.forEach((msg) => {
      if (
        msg.sender.userId === currentUserId &&
        msg.status !== 'read' &&
        (!upToMessageId || msg._id <= upToMessageId)
      ) {
        state._updateMessage(msg._id, { status: 'read' });
      }
    });
  });
});

// Handle typing indicators
wsClient.on('typing:start', (payload) => {
  const { userId, userName } = payload as { userId: string; userName: string };
  console.log('[Chat Store WS] ⌨️ typing:start event:', { userId, userName });
  const conversationId = useChatStore.getState().activeConversationId;
  if (conversationId) {
    useChatStore.getState()._addTypingUser(conversationId, { userId, userName });
  }
});

wsClient.on('typing:stop', (payload) => {
  const { userId } = payload as { userId: string };
  const conversationId = useChatStore.getState().activeConversationId;
  if (conversationId) {
    useChatStore.getState()._removeTypingUser(conversationId, userId);
  }
});

// Handle presence updates
wsClient.on('presence:update', (payload) => {
  const { userId, status } = payload as { userId: string; status: string };
  useChatStore.getState()._setOnlineUser(userId, status !== 'offline');
});

wsClient.on('participant:online', (payload) => {
  const { userId } = payload as { userId: string };
  useChatStore.getState()._setOnlineUser(userId, true);
});

wsClient.on('participant:offline', (payload) => {
  const { userId } = payload as { userId: string };
  useChatStore.getState()._setOnlineUser(userId, false);
});

// Handle unread sync
wsClient.on('unread:sync', (payload) => {
  const { unreadCounts } = payload as { unreadCounts: Record<string, number> };
  useChatStore.getState()._updateUnreadCounts(unreadCounts);

  // If the server reports unread for the currently-open conversation, clear it immediately
  const activeId = useChatStore.getState().activeConversationId;
  if (activeId && unreadCounts[activeId] > 0) {
    useChatStore.getState().markAsRead(activeId);
  }
});

// Handle subscription confirmation with typing users
wsClient.on('subscribed', (payload) => {
  const { conversationId, typingUsers } = payload as {
    conversationId: string;
    typingUsers: TypingUser[];
  };
  console.log('[Chat Store WS] 🔔 subscribed event:', { conversationId, typingUsersCount: typingUsers?.length });
  useChatStore.getState()._setTypingUsers(conversationId, typingUsers);
});

// Handle connection/disconnection events for polling
wsClient.on('connect', () => {
  console.log('[Chat Store WS] 🔌 Connected, stopping polling');
  useChatStore.getState().stopPolling();
});

// After auth succeeds, subscribe to all known conversations so the chat list
// receives real-time message:new events without needing to open each conversation
wsClient.on('auth:success', () => {
  const conversations = useChatStore.getState().conversations;
  if (conversations.length > 0) {
    console.log(`[Chat Store WS] 📡 Auto-subscribing to ${conversations.length} conversations for real-time list updates`);
    conversations.forEach(c => wsClient.subscribe(c._id));
  }
});

wsClient.on('disconnect', () => {
  console.log('[Chat Store WS] 🔌 Disconnected, starting polling if active conversation exists');
  const { activeConversationId, startPolling } = useChatStore.getState();
  if (activeConversationId) {
    startPolling();
  }
});
